import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaEdit, FaTrash } from "react-icons/fa";
import { deleteCompanyAccount, fetchCompanyAccounts } from "./companyAccountService";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useConfirm } from "../components/ConfirmModal";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";
import "../assets/css/DigixLeads.css";
import "../assets/css/FinanceAccounts.css";

const ROWS_PER_PAGE = 10;

function formatDateDisplay(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "—";
  }
}

function FinanceAccounts() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [filters, setFilters] = useState({
    searchText: "",
    status: "all",
    type: "all"
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [columnWidths, setColumnWidths] = useState({});
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const resizeRef = useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const [deletingId, setDeletingId] = useState(null);
  const [freeSearch, setFreeSearch] = useState("");

  const loadAccounts = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const list = await fetchCompanyAccounts();
      setAccounts(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error(err);
      setListError(err.response?.data?.message || err.message || "Could not load company accounts.");
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    const query = filters.searchText.trim().toLowerCase();
    return accounts.filter((row) => {
      const accountName = String(row.Account_Name ?? row.account_name ?? "").toLowerCase();
      const bankName = String(row.Bank_Name ?? row.bank_name ?? "").toLowerCase();
      const accountNumber = String(row.Account_Number ?? row.account_number ?? "").toLowerCase();
      const status = String(row.Status ?? row.status ?? "").toLowerCase();
      const type = String(row.Account_Type ?? row.account_type ?? "").toLowerCase();

      const matchesQuery =
        !query ||
        accountName.includes(query) ||
        bankName.includes(query) ||
        accountNumber.includes(query);
      const matchesStatus = filters.status === "all" || status === filters.status;
      const matchesType = filters.type === "all" || type === filters.type;
      return matchesQuery && matchesStatus && matchesType;
    });
  }, [accounts, filters]);

  const freeSearchFiltered = useMemo(() => {
    const q = freeSearch.trim().toLowerCase();
    if (!q) return filteredAccounts;
    return filteredAccounts.filter((row) => {
      const primary =
        row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1 ? "Yes" : "No";
      const created = formatDateDisplay(row.Created_On ?? row.created_on);
      const haystack = [
        row.Account_Name ?? row.account_name,
        row.Bank_Name ?? row.bank_name,
        row.Account_Number ?? row.account_number,
        row.Account_Type ?? row.account_type,
        row.IFSC_Code ?? row.ifsc_code,
        row.Branch ?? row.branch,
        row.Currency_Code ?? row.currency_code ?? row.Currency_Id,
        row.Status ?? row.status,
        row.Notes ?? row.notes,
        primary,
        created,
        String(row.Company_Account_Id ?? row.company_account_id ?? "")
      ]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return haystack.includes(q);
    });
  }, [filteredAccounts, freeSearch]);

  const sortedAccounts = useMemo(() => {
    if (!sortColumn) return freeSearchFiltered;
    const val = (row, key) => {
      switch (key) {
        case "accountName":
          return String(row.Account_Name ?? row.account_name ?? "").toLowerCase();
        case "bank":
          return String(row.Bank_Name ?? row.bank_name ?? "").toLowerCase();
        case "number":
          return String(row.Account_Number ?? row.account_number ?? "").toLowerCase();
        case "type":
          return String(row.Account_Type ?? row.account_type ?? "").toLowerCase();
        case "ifsc":
          return String(row.IFSC_Code ?? row.ifsc_code ?? "").toLowerCase();
        case "branch":
          return String(row.Branch ?? row.branch ?? "").toLowerCase();
        case "currency":
          return String(row.Currency_Code ?? row.currency_code ?? row.Currency_Id ?? "").toLowerCase();
        case "primary":
          return row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1 ? "yes" : "no";
        case "status":
          return String(row.Status ?? row.status ?? "").toLowerCase();
        case "created":
          return String(row.Created_On ?? row.created_on ?? "");
        default:
          return "";
      }
    };
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFiltered].sort((a, b) => {
      const va = val(a, sortColumn);
      const vb = val(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.Company_Account_Id ?? a.company_account_id ?? 0);
      const idb = Number(b.Company_Account_Id ?? b.company_account_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFiltered, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedAccounts.length / ROWS_PER_PAGE));
  const startIndex = (currentPage - 1) * ROWS_PER_PAGE;
  const endIndex = startIndex + ROWS_PER_PAGE;
  const paginatedAccounts = sortedAccounts.slice(startIndex, endIndex);
  const showPagination = sortedAccounts.length >= ROWS_PER_PAGE;
  const startRow = sortedAccounts.length > 0 ? startIndex + 1 : 0;
  const endRow = Math.min(endIndex, sortedAccounts.length);
  const totalRows = sortedAccounts.length;

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, freeSearch]);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [sortedAccounts.length, currentPage, totalPages]);

  const handleSort = (columnId) => {
    setCurrentPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, "...", totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((p) => p + 1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const getColumnWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) return `${columnWidths[columnIndex]}px`;
    return undefined;
  };

  const getColumnMinWidth = (columnIndex) => {
    if (columnWidths[columnIndex]) return `${columnWidths[columnIndex]}px`;
    return undefined;
  };

  const handleResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();

    const th = e.currentTarget.parentElement;
    if (!th) return;

    resizeRef.current = {
      startX: e.clientX,
      startWidth: th.offsetWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (resizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - resizeRef.current.startX;
      const newWidth = Math.max(90, resizeRef.current.startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [resizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleDeleteAccount = async (accountId) => {
    if (!accountId) return;
    const ok = await confirm({
      title: "Deactivate Company Account",
      message: "Are you sure you want to deactivate this company account?",
    });
    if (!ok) return;
    setDeletingId(accountId);
    try {
      const res = await deleteCompanyAccount(accountId);
      await loadAccounts();
      const msg = res?.message || "Company account deactivated.";
      window.alert(msg);
    } catch (err) {
      console.error(err);
      window.alert(err.response?.data?.message || err.response?.data?.error || "Failed to deactivate company account.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="finance-accounts">
      <div className="search-actions-row finance-accounts-search-actions-row">
        <div className="search-filters-row">
          <input
            id="finance-accounts-search"
            className="search-filter-input"
            type="text"
            value={filters.searchText}
            onChange={(e) => setFilters((prev) => ({ ...prev, searchText: e.target.value }))}
            placeholder="Search account, bank, or number..."
          />
          <select
            id="finance-accounts-status-filter"
            className="search-filter-input search-filter-select"
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="all">Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            id="finance-accounts-type-filter"
            className="search-filter-input search-filter-select"
            value={filters.type}
            onChange={(e) => setFilters((prev) => ({ ...prev, type: e.target.value }))}
          >
            <option value="all">Type</option>
            <option value="savings">Savings</option>
            <option value="current">Current</option>
          </select>
        <button
          type="button"
          className="btn-clear"
          onClick={() => {
            const reset = { searchText: "", status: "all", type: "all" };
            setFilters(reset);
            setFreeSearch("");
          }}
        >
            Clear
        </button>
        </div>
        <div className="finance-accounts-actions header-actions">
          <button
            type="button"
            className="btn-primary btn-add-lead btn-new-company-account"
            onClick={() => navigate("/finance-management/accounts/new")}
          >
            New Account
          </button>
        </div>
      </div>

      <div className="search-row-2">
        <div className="free-search-bar">
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={freeSearch}
            onChange={(e) => setFreeSearch(e.target.value)}
            title="Search across all columns"
          />
        </div>
      </div>

      {listError && <div className="finance-accounts-alert finance-accounts-alert--error">{listError}</div>}

      {loading ? (
        <div className="finance-accounts-loading">Loading accounts...</div>
      ) : (
        <>
        <div className="finance-accounts-table-wrap co-desktop-table">
          <table className="finance-accounts-table finance-accounts-table-resizable">
            <thead>
              <tr>
                <th
                  className="col-sticky col-0 finance-th-sortable"
                  style={{ minWidth: getColumnMinWidth(0) || "140px", width: getColumnWidth(0), position: "relative" }}
                >
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="accountName" label="Account name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 0)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(1) || "120px", width: getColumnWidth(1), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="bank" label="Bank" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 1)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(2) || "130px", width: getColumnWidth(2), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="number" label="Number" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 2)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(3) || "110px", width: getColumnWidth(3), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="type" label="Type" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 3)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="ifsc" label="IFSC" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 4)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(5) || "110px", width: getColumnWidth(5), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="branch" label="Branch" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 5)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(6) || "100px", width: getColumnWidth(6), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="currency" label="Currency" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 6)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(7) || "90px", width: getColumnWidth(7), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="primary" label="Primary" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 7)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(8) || "100px", width: getColumnWidth(8), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 8)}></span>
                </th>
                <th className="finance-th-sortable" style={{ minWidth: getColumnMinWidth(9) || "120px", width: getColumnWidth(9), position: "relative" }}>
                  <div className="finance-th-sort-row">
                    <SupportSortableHeader columnId="created" label="Created" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                  </div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 9)}></span>
                </th>
                <th
                  className="col-sticky col-action"
                  style={{ minWidth: getColumnMinWidth(10) || "110px", width: getColumnWidth(10), position: "relative" }}
                >
                  <div style={{ justifyContent: "center" }}>Action</div>
                  <span className="column-resizer" onMouseDown={(e) => handleResizeStart(e, 10)}></span>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length === 0 ? (
                <tr>
                  <td colSpan={11} className="finance-accounts-empty">
                    No accounts found for selected filters.
                  </td>
                </tr>
              ) : freeSearchFiltered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="finance-accounts-empty">
                    No accounts match your search.
                  </td>
                </tr>
              ) : (
                paginatedAccounts.map((row, idx) => {
                  const id = row.Company_Account_Id ?? row.company_account_id;
                  return (
                    <tr key={id != null ? String(id) : `row-${idx}`}>
                      <td
                        className="col-sticky col-0"
                        style={{ minWidth: getColumnMinWidth(0) || "140px", width: getColumnWidth(0) }}
                      >
                        {id != null ? (
                          <span
                            className="table-name-link"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigate(`/finance-management/accounts/${id}/view`)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/finance-management/accounts/${id}/view`);
                              }
                            }}
                          >
                            {row.Account_Name ?? row.account_name ?? "—"}
                          </span>
                        ) : (
                          row.Account_Name ?? row.account_name ?? "—"
                        )}
                      </td>
                      <td style={{ minWidth: getColumnMinWidth(1) || "120px", width: getColumnWidth(1) }}>{row.Bank_Name ?? row.bank_name ?? "—"}</td>
                      <td className="finance-accounts-mono" style={{ minWidth: getColumnMinWidth(2) || "130px", width: getColumnWidth(2) }}>{row.Account_Number ?? row.account_number ?? "—"}</td>
                      <td style={{ minWidth: getColumnMinWidth(3) || "110px", width: getColumnWidth(3) }}>{row.Account_Type ?? row.account_type ?? "—"}</td>
                      <td className="finance-accounts-mono" style={{ minWidth: getColumnMinWidth(4) || "120px", width: getColumnWidth(4) }}>{row.IFSC_Code ?? row.ifsc_code ?? "—"}</td>
                      <td style={{ minWidth: getColumnMinWidth(5) || "110px", width: getColumnWidth(5) }}>{row.Branch ?? row.branch ?? "—"}</td>
                      <td style={{ minWidth: getColumnMinWidth(6) || "100px", width: getColumnWidth(6) }}>{row.Currency_Code ?? row.currency_code ?? row.Currency_Id ?? "—"}</td>
                      <td style={{ minWidth: getColumnMinWidth(7) || "90px", width: getColumnWidth(7) }}>
                        {row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1 ? (
                          <span className="finance-accounts-badge finance-accounts-badge--yes">Yes</span>
                        ) : (
                          <span className="finance-accounts-badge">No</span>
                        )}
                      </td>
                      <td style={{ minWidth: getColumnMinWidth(8) || "100px", width: getColumnWidth(8) }}>
                        <span
                          className={`finance-accounts-status finance-accounts-status--${String(
                            row.Status ?? row.status ?? ""
                          )
                            .toLowerCase()
                            .replace(/\s/g, "-")}`}
                        >
                          {row.Status ?? row.status ?? "—"}
                        </span>
                      </td>
                      <td style={{ minWidth: getColumnMinWidth(9) || "120px", width: getColumnWidth(9) }}>{formatDateDisplay(row.Created_On ?? row.created_on)}</td>
                      <td
                        className="col-sticky col-action"
                        style={{ minWidth: getColumnMinWidth(10) || "110px", width: getColumnWidth(10), textAlign: "center" }}
                      >
                        {id != null ? (
                          <div className="finance-account-actions-cell">
                            <button
                              type="button"
                              className="finance-account-action-btn finance-account-action-btn-edit"
                              title="Edit account"
                              onClick={() => navigate(`/finance-management/accounts/${id}/edit`)}
                            >
                              <FaEdit />
                            </button>
                            <button
                              type="button"
                              className="finance-account-action-btn finance-account-action-btn-delete"
                              title="Deactivate account"
                              onClick={() => handleDeleteAccount(id)}
                              disabled={deletingId === id}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <ResultsMobileList
          emptyLabel={
            filteredAccounts.length === 0
              ? "No accounts found for selected filters."
              : "No accounts match your search."
          }
          className="finance-accounts-mobile"
        >
          {(freeSearchFiltered.length ? paginatedAccounts : []).map((row, idx) => {
            const id = row.Company_Account_Id ?? row.company_account_id;
            const status = row.Status ?? row.status ?? "—";
            const statusTone = String(status).toLowerCase() === "active" ? "success" : "neutral";
            const isPrimary =
              row.Is_Primary === true || row.Is_Primary === 1 || row.is_primary === 1;
            return (
              <ResultsMobileCard
                key={id != null ? String(id) : `row-${idx}`}
                title={row.Account_Name ?? row.account_name ?? "—"}
                subtitle={row.Bank_Name ?? row.bank_name ?? null}
                meta={[
                  { label: status, tone: statusTone },
                  isPrimary ? { label: "Primary", tone: "info" } : null,
                ].filter(Boolean)}
                fields={[
                  { label: "Number", value: row.Account_Number ?? row.account_number },
                  { label: "Type", value: row.Account_Type ?? row.account_type },
                  { label: "IFSC", value: row.IFSC_Code ?? row.ifsc_code },
                  { label: "Branch", value: row.Branch ?? row.branch },
                  {
                    label: "Currency",
                    value: row.Currency_Code ?? row.currency_code ?? row.Currency_Id,
                  },
                ]}
                onOpen={
                  id != null
                    ? () => navigate(`/finance-management/accounts/${id}/view`)
                    : undefined
                }
                actions={
                  id != null ? (
                    <div className="co-mcard__action-grid">
                      <button
                        type="button"
                        className="co-maction"
                        title="Edit account"
                        aria-label="Edit account"
                        onClick={() => navigate(`/finance-management/accounts/${id}/edit`)}
                      >
                        <FaEdit size={14} />
                        <span>Edit</span>
                      </button>
                      <button
                        type="button"
                        className="co-maction co-maction--danger"
                        title="Deactivate account"
                        aria-label="Deactivate account"
                        onClick={() => handleDeleteAccount(id)}
                        disabled={deletingId === id}
                      >
                        <FaTrash size={14} />
                        <span>Delete</span>
                      </button>
                    </div>
                  ) : null
                }
              />
            );
          })}
        </ResultsMobileList>
        </>
      )}
      {!loading && showPagination && (
        <div className="pagination">
          <button
            type="button"
            className="pagination-btn pagination-arrow"
            onClick={handlePreviousPage}
            disabled={currentPage === 1}
            aria-label="Previous page"
          >
            <FaChevronLeft />
          </button>
          <div className="page-numbers">
            {getPageNumbers().map((page, index) =>
              page === "..." ? (
                <span key={`ellipsis-${index}`} className="page-number ellipsis">
                  ...
                </span>
              ) : (
                <span
                  key={page}
                  role="button"
                  tabIndex={0}
                  className={`page-number ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageClick(page)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handlePageClick(page);
                    }
                  }}
                >
                  {page}
                </span>
              )
            )}
          </div>
          <button
            type="button"
            className="pagination-btn pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage === totalPages || totalPages === 0}
            aria-label="Next page"
          >
            <FaChevronRight />
          </button>
          {totalRows > 0 && (
            <div className="pagination-info">
              {startRow}-{endRow} of {totalRows} items
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default FinanceAccounts;
